import { useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { errorMessage } from "@/lib/api";
import { EMAIL_RE } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import { homeFor } from "@/components/RequireRole";
import { FieldError, FullPageLoader, usePageTitle } from "@/components/States";
import { Brand } from "@/components/layout/SidebarShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Login() {
  usePageTitle("Sign in");
  const { profile, loading, blocked, signIn } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [form, setForm] = useState({ email: "", password: "" });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);

  if (loading) return <FullPageLoader />;
  if (profile) return <Navigate to={homeFor(profile)} replace />;

  const submit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!EMAIL_RE.test(form.email.trim())) errs.email = "Enter a valid email address";
    if (!form.password) errs.password = "Enter your password";
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setBusy(true);
    try {
      const user = await signIn(form.email.trim(), form.password);
      const from = location.state?.from;
      const returnToRun = from?.startsWith("/runs/");
      navigate(from && (from.startsWith(homeFor(user)) || returnToRun) ? from : homeFor(user), { replace: true });
    } catch (err) {
      setErrors({ form: errorMessage(err) });
      setBusy(false);
    }
  };

  const formError = errors.form || blocked;
  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-900 p-6">
      <form onSubmit={submit} noValidate className="w-full max-w-md space-y-5 rounded-xl border border-slate-200 bg-white p-8 shadow-xl" data-testid="login-form">
          <div className="inline-block rounded-lg bg-slate-900 p-3" data-testid="login-logo"><Brand roleLabel="For warrant teams" /></div>
          <div>
            <h1 className="font-display text-2xl font-extrabold text-slate-900">Sign in</h1>
            <p className="mt-1 text-sm text-slate-500">Team accounts are invite only.</p>
          </div>
          <div>
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" autoComplete="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5 h-11" data-testid="login-email-input" />
            <FieldError message={errors.email} testId="login-email-error" />
          </div>
          <div>
            <Label htmlFor="password">Password</Label>
            <Input id="password" type="password" autoComplete="current-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1.5 h-11" data-testid="login-password-input" />
            <FieldError message={errors.password} testId="login-password-error" />
          </div>
          {formError && <p role="alert" className="rounded-md bg-rose-50 p-3 text-sm text-rose-700" data-testid="login-form-error">{formError}</p>}
          <Button type="submit" disabled={busy} className="h-11 w-full bg-blue-700 hover:bg-blue-800" data-testid="login-submit-button">
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Sign in
          </Button>
          <p className="text-center text-sm"><Link to="/forgot-password" className="font-semibold text-blue-700 hover:underline" data-testid="login-forgot-password-link">Forgot your password?</Link></p>
          <p className="text-center text-sm text-slate-600">New to Warrant Whizz? <Link to="/signup" className="font-semibold text-blue-700 hover:underline" data-testid="login-signup-link">Sign up your company</Link></p>
        </form>
    </div>
  );
}
